<script setup lang="ts">
import {
  breakpointsTailwind,
  useBreakpoints,
  useScreenOrientation,
} from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import CFooter from './CFooter.vue'
import CHeader from './CHeader.vue'
import CSidebar from './CSidebar.vue'

const drawerOpenState = ref(false)

// Use VueUse composables for screen orientation and breakpoints
const { orientation: screenOrientation, isSupported: isOrientationSupported }
  = useScreenOrientation()
const breakpoints = useBreakpoints(breakpointsTailwind)

// Check if device is mobile/tablet (smaller than md breakpoint, which is 768px)
// Using smaller('lg') to catch larger phones in landscape (like iPhone 13 Pro Max ~926px)
const isMobileDevice = computed(() => {
  return breakpoints.smaller('lg') // lg is 1024px in Tailwind
})

// Check if device is in landscape orientation
const isLandscape = computed(() => {
  // Use Screen Orientation API if available
  if (isOrientationSupported.value && screenOrientation.value) {
    return screenOrientation.value.startsWith('landscape')
  }
  // Fallback: check if width > height
  if (typeof window !== 'undefined') {
    return window.innerWidth > window.innerHeight
  }
  return false
})

// Computed property to check if device is mobile in landscape mode
// Hide drawer in landscape mode on mobile devices
const isLandscapeMobile = computed(() => {
  return isMobileDevice.value && isLandscape.value
})

// Computed property that prevents drawer from being open in landscape
const drawerOpen = computed({
  get: () => {
    // If in landscape mobile, always return false
    if (isLandscapeMobile.value) {
      return false
    }
    return drawerOpenState.value
  },
  set: (value: boolean) => {
    // If trying to open in landscape, don't allow it
    if (value && isLandscapeMobile.value) {
      drawerOpenState.value = false
      return
    }
    drawerOpenState.value = value
  },
})

// Watch for landscape orientation and close drawer immediately
watch(
  isLandscapeMobile,
  (landscape) => {
    if (landscape) {
      drawerOpenState.value = false
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="relative h-screen w-screen flex overflow-hidden">
    <!-- Fixed Sidebar - Desktop Only -->
    <aside
      class="hidden md:block fixed left-0 top-0 h-screen w-64 overflow-y-auto overflow-x-hidden"
    >
      <CSidebar />
    </aside>

    <!-- Drawer Sidebar - Mobile/Tablet -->
    <el-drawer
      v-model="drawerOpen"
      direction="ltr"
      :size="256"
      :show-close="false"
      :modal="true"
      :close-on-click-modal="true"
      class="md:hidden !bg-[#F2F6FC]"
    >
      <CSidebar @close="drawerOpen = false" />
    </el-drawer>

    <!-- Main Content Area (with margin-left for sidebar on desktop) -->
    <div class="flex-1 flex flex-col min-w-0 md:ml-64 h-screen">
      <!-- Hamburger Button - Tablet Only (sm-md), mobile handled by CHeader 2-row -->
      <button
        class="hidden sm:block md:hidden fixed top-6 left-4 z-50 p-2 rounded-lg bg-white shadow-md hover:bg-gray-50 transition-colors"
        aria-label="Open menu"
        @click="drawerOpen = true"
      >
        <svg
          class="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      <!-- Sticky Header -->
      <header class="sticky top-0 flex-shrink-0 z-10">
        <CHeader @toggle-sidebar="drawerOpen = true" />
      </header>

      <!-- Scrollable Main Content -->
      <main class="flex-1 overflow-y-auto min-h-0">
        <div class="lg:px-4 sm:pr-2 md:pt-2 h-full">
          <slot />
        </div>
      </main>

      <!-- Sticky Footer -->
      <footer
        class="sticky bottom-0 z-0 lg:px-4 pr-2 flex-shrink-0 hidden md:block"
      >
        <CFooter />
      </footer>
    </div>
  </div>
</template>

<style scoped>
:deep(.el-drawer__header) {
  display: none;
}

:deep(.el-drawer__body) {
  padding: 0;
}
</style>
